import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, RotateCcw, Clock, FileText, Code, 
  ChevronDown, ChevronRight, AlertTriangle, Check, History,
  Sparkles, Image, Type
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useTheme } from '../contexts/ThemeContext';
import { API } from '../App';

const PromptsManager = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedService, setExpandedService] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [promptDetail, setPromptDetail] = useState(null);
  const [editedValue, setEditedValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchPrompts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/prompts`);
      
      setServices(data.services || []);
      // Auto-expand first service
      if (data.services && data.services.length > 0) {
        setExpandedService(data.services[0].service_id);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      if (err.response?.status === 403) {
        toast.error('Accès réservé aux Amiraux');
        navigate('/');
      } else {
        toast.error('Erreur chargement des prompts');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  const loadPromptDetail = async (serviceId, promptId) => {
    try {
      const { data } = await axios.get(`${API}/admin/prompts/${serviceId}/${promptId}`);
      setPromptDetail(data);
      setEditedValue(data.prompt.current_value || '');
      setSelectedPrompt({ serviceId, promptId });
      setHasChanges(false);
    } catch (err) {
      toast.error('Erreur chargement du prompt');
    }
  };

  const handleSave = async () => {
    if (!selectedPrompt || !hasChanges) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/prompts/${selectedPrompt.serviceId}/${selectedPrompt.promptId}`,
        { new_value: editedValue }
      );
      toast.success('Prompt mis à jour');
      setHasChanges(false);
      await loadPromptDetail(selectedPrompt.serviceId, selectedPrompt.promptId);
      await fetchPrompts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (version) => {
    if (!selectedPrompt) return;
    
    const label = version === 'default' ? 'la version originale' : 'cette version';
    if (!window.confirm(`Restaurer ${label} ? La version actuelle sera sauvegardée dans l'historique.`)) {
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/prompts/${selectedPrompt.serviceId}/${selectedPrompt.promptId}/restore`,
        { version }
      );
      toast.success('Prompt restauré');
      await loadPromptDetail(selectedPrompt.serviceId, selectedPrompt.promptId);
      await fetchPrompts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la restauration');
    } finally {
      setSaving(false);
    }
  };

  const handleEditorChange = (value) => {
    setEditedValue(value);
    setHasChanges(value !== (promptDetail?.prompt?.current_value || ''));
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-slate-950 text-slate-100' : 'bg-gray-50 text-gray-900'}`}>
        <div className="animate-pulse text-amber-500">Chargement des prompts...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-950 text-slate-100' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 backdrop-blur border-b ${isDark ? 'bg-slate-900/95 border-slate-800' : 'bg-white/95 border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/profile')}
            className={isDark ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au profil
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Gestion des Prompts
              <span className="text-xs bg-amber-500/20 text-amber-600 dark:text-amber-300 px-2 py-0.5 rounded-full ml-2">
                Amiraux
              </span>
            </h1>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
              Modifiez les prompts LLM utilisés dans toute l'application
            </p>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar - Liste des services et prompts */}
          <div className="col-span-4 space-y-2">
            <h2 className={`text-sm font-medium mb-3 flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
              <Code className="w-4 h-4" />
              Services & Prompts ({services.length})
            </h2>
            
            {/* Légende des types */}
            <div className={`flex items-center gap-4 text-xs mb-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
              <span className="flex items-center gap-1">
                <Type className="w-3 h-3 text-blue-500" /> Texte (LLM)
              </span>
              <span className="flex items-center gap-1">
                <Image className="w-3 h-3 text-purple-500" /> Image (IA)
              </span>
            </div>
            
            {services.length === 0 ? (
              <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-900/50 border-slate-800 text-slate-400' : 'bg-white border-gray-200 text-gray-500'}`}>
                Aucun prompt configuré
              </div>
            ) : (
              services.map((service) => {
                const isImageService = service.service_id === 'images';
                return (
                <div key={service.service_id} className={`rounded-lg border overflow-hidden ${
                  isImageService 
                    ? (isDark ? 'bg-purple-900/20 border-purple-800/50' : 'bg-purple-50 border-purple-200')
                    : (isDark ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-gray-200')
                }`}>
                  <button
                    onClick={() => setExpandedService(
                      expandedService === service.service_id ? null : service.service_id
                    )}
                    className={`w-full px-4 py-3 flex items-center justify-between transition ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-gray-50'}`}
                  >
                    <span className="font-medium text-sm flex items-center gap-2">
                      {isImageService && <Image className="w-4 h-4 text-purple-500" />}
                      {service.service_name}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                        {service.prompts.length} prompt{service.prompts.length > 1 ? 's' : ''}
                      </span>
                      {expandedService === service.service_id ? (
                        <ChevronDown className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                      ) : (
                        <ChevronRight className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                      )}
                    </div>
                  </button>
                  
                  {expandedService === service.service_id && (
                    <div className={`border-t ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
                      {service.prompts.map((prompt) => {
                        const isImagePrompt = prompt.prompt_type === 'image';
                        return (
                        <button
                          key={prompt.prompt_id}
                          onClick={() => loadPromptDetail(service.service_id, prompt.prompt_id)}
                          className={`w-full px-4 py-2 text-left transition flex items-center gap-2 ${
                            selectedPrompt?.promptId === prompt.prompt_id 
                              ? (isImagePrompt 
                                  ? (isDark ? 'bg-purple-500/20 border-l-2 border-purple-500' : 'bg-purple-100 border-l-2 border-purple-500')
                                  : (isDark ? 'bg-amber-500/10 border-l-2 border-amber-500' : 'bg-amber-50 border-l-2 border-amber-500'))
                              : (isDark ? 'hover:bg-slate-800/50' : 'hover:bg-gray-50')
                          }`}
                        >
                          {isImagePrompt ? (
                            <Image className="w-3.5 h-3.5 flex-shrink-0 text-purple-500" />
                          ) : (
                            <Type className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate">{prompt.name}</div>
                            <div className="flex items-center gap-2">
                              {prompt.is_modified && (
                                <span className="text-[10px] text-amber-500">modifié</span>
                              )}
                            </div>
                          </div>
                        </button>
                      )})}
                    </div>
                  )}
                </div>
              )})
            )}
          </div>

          {/* Main content - Éditeur de prompt */}
          <div className="col-span-8">
            {promptDetail ? (
              <div className={`rounded-lg border overflow-hidden ${
                promptDetail.prompt.prompt_type === 'image'
                  ? (isDark ? 'bg-purple-900/20 border-purple-800/50' : 'bg-purple-50/50 border-purple-200')
                  : (isDark ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-gray-200')
              }`}>
                {/* Header du prompt */}
                <div className={`px-4 py-3 border-b ${isDark ? 'border-slate-800 bg-slate-800/30' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium flex items-center gap-2">
                        {promptDetail.prompt.prompt_type === 'image' ? (
                          <Image className="w-4 h-4 text-purple-500" />
                        ) : (
                          <Type className="w-4 h-4 text-blue-500" />
                        )}
                        {promptDetail.prompt.name}
                        {/* Badge type de prompt */}
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          promptDetail.prompt.prompt_type === 'image'
                            ? 'bg-purple-500/20 text-purple-600 dark:text-purple-300'
                            : 'bg-blue-500/20 text-blue-600 dark:text-blue-300'
                        }`}>
                          {promptDetail.prompt.prompt_type === 'image' ? '🎨 Image IA' : '📝 Texte LLM'}
                        </span>
                        {promptDetail.prompt.is_modified && (
                          <span className="text-xs bg-amber-500/20 text-amber-600 dark:text-amber-300 px-2 py-0.5 rounded">
                            Modifié
                          </span>
                        )}
                      </h3>
                      <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                        {promptDetail.prompt.description}
                      </p>
                      <p className={`text-xs mt-1 font-mono ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
                        📍 {promptDetail.prompt.location}
                      </p>
                      {/* Info sur les variables pour les prompts image */}
                      {promptDetail.prompt.prompt_type === 'image' && (
                        <p className={`text-xs mt-2 p-2 rounded ${isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                          💡 Variables disponibles : <code className="font-mono">{'{title}'}</code> = titre de l'article, <code className="font-mono">{'{key_figure}'}</code> = chiffre clé (infographie)
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRestore('default')}
                        disabled={saving || !promptDetail.prompt.is_modified}
                        className="text-xs"
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                        Original
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                        className={`text-white text-xs ${
                          promptDetail.prompt.prompt_type === 'image'
                            ? 'bg-purple-600 hover:bg-purple-500'
                            : 'bg-amber-600 hover:bg-amber-500'
                        }`}
                      >
                        {saving ? (
                          <span className="animate-pulse">Sauvegarde...</span>
                        ) : (
                          <>
                            <Save className="w-3.5 h-3.5 mr-1" />
                            Enregistrer
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Éditeur */}
                <div className="p-4">
                  <Textarea
                    value={editedValue}
                    onChange={(e) => handleEditorChange(e.target.value)}
                    className={`font-mono text-xs min-h-[400px] resize-y ${isDark ? 'bg-slate-950 border-slate-700' : 'bg-gray-50 border-gray-300'}`}
                    placeholder="Contenu du prompt..."
                  />
                  
                  {hasChanges && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-amber-500">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Modifications non enregistrées
                    </div>
                  )}
                </div>

                {/* Historique */}
                {(promptDetail.history?.length > 0 || promptDetail.prompt.is_modified) && (
                  <div className={`px-4 py-3 border-t ${isDark ? 'border-slate-800 bg-slate-800/20' : 'border-gray-200 bg-gray-50'}`}>
                    <h4 className={`text-xs font-medium mb-2 flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                      <History className="w-3.5 h-3.5" />
                      Versions précédentes
                    </h4>
                    <div className="space-y-2">
                      {promptDetail.prompt.is_modified && (
                        <div className={`flex items-center justify-between p-2 rounded text-xs ${isDark ? 'bg-slate-800/50' : 'bg-white border border-gray-200'}`}>
                          <div className="flex items-center gap-2">
                            <Check className="w-3.5 h-3.5 text-emerald-500" />
                            <span>Version originale (défaut)</span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRestore('default')}
                            className="text-xs h-7"
                          >
                            Restaurer
                          </Button>
                        </div>
                      )}
                      {promptDetail.history?.map((h, i) => (
                        <div key={i} className={`flex items-center justify-between p-2 rounded text-xs ${isDark ? 'bg-slate-800/50' : 'bg-white border border-gray-200'}`}>
                          <div className="flex items-center gap-2">
                            <Clock className={`w-3.5 h-3.5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                            <span>
                              {new Date(h.created_at).toLocaleString('fr-FR')}
                              {h.updated_by && (
                                <span className={isDark ? 'text-slate-500 ml-1' : 'text-gray-400 ml-1'}>
                                  par {h.updated_by}
                                </span>
                              )}
                            </span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRestore(h.created_at)}
                            className="text-xs h-7"
                          >
                            Restaurer
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className={`rounded-lg border p-8 text-center ${isDark ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-gray-200'}`}>
                <Sparkles className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-slate-700' : 'text-gray-300'}`} />
                <h3 className={`text-lg font-medium ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Sélectionnez un prompt</h3>
                <p className={`text-sm mt-2 ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
                  Choisissez un service et un prompt dans la liste de gauche pour le modifier.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptsManager;
